import { Box, HStack, Text } from "@chakra-ui/layout";
import { Link } from "@chakra-ui/react";
import { FC } from "react";
import { graphql, useFragment } from "react-relay";
import { NavLink } from "react-router-dom";

import { AccountDetailPage_viewer$key } from "~/src/__relay_artifacts__/AccountDetailPage_viewer.graphql";
import { OutlineButton } from "~/src/components/common/Button";
import { DefinitionTeam } from "~/src/components/common/DefinitionTeam";
import { SectionCard } from "~/src/components/common/SectionCard";
import { PageLayout } from "~/src/components/features/global/PageLayout";

import { AccountSwitch } from "./AccountSwitch";

export type Props = {
  userRef: AccountDetailPage_viewer$key;
};

const fragment = graphql`
  fragment AccountDetailPage_viewer on User {
    id
    name
    email
    company
    tel
    itpUrls
    userTag
    role
    isCrossDomainQuery
    isCrossDomainFragment
    jsTagManagementEnabled
    enableBackendUrlMapping
    sites(first: 1) {
      nodes {
        slug
      }
    }
  }
`;

export const AccountDetailPage: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ userRef }) => {
  const user = useFragment(fragment, userRef);
  const firstSiteSlug = user.sites.nodes?.[0]?.slug;
  const scriptTagSettingsPath =
    firstSiteSlug != null
      ? `/sites/${firstSiteSlug}/settings?tab=script_tag`
      : null;
  return (
    <PageLayout title="アカウント設定">
      <Box my="16px">
        <HStack justifyContent="flex-end" mb="16px" spacing="16px">
          <NavLink to={`/account/edit`}>
            <OutlineButton>編集</OutlineButton>
          </NavLink>
        </HStack>
      </Box>

      <SectionCard>
        <DefinitionTeam label="会社名">{user.company}</DefinitionTeam>
        <DefinitionTeam label="担当者名">{user.name}</DefinitionTeam>
        <DefinitionTeam label="メールアドレス">{user.email}</DefinitionTeam>
        <DefinitionTeam label="電話番号">{user.tel}</DefinitionTeam>
        <DefinitionTeam label="ITP対応用エンドポイント">
          {user.itpUrls || "未設定"}
        </DefinitionTeam>
        <DefinitionTeam label="クロスドメイン(クエリ)">
          <AccountSwitch
            isChecked={user.isCrossDomainQuery}
            input={{ isCrossDomainQuery: !user.isCrossDomainQuery }}
          />
        </DefinitionTeam>
        <DefinitionTeam label="クロスドメイン(ハッシュ文字)">
          <AccountSwitch
            isChecked={user.isCrossDomainFragment}
            input={{ isCrossDomainFragment: !user.isCrossDomainFragment }}
          />
        </DefinitionTeam>
        <DefinitionTeam label="バックエンドURLマッピング">
          <AccountSwitch
            isChecked={user.enableBackendUrlMapping}
            input={{
              enableBackendUrlMapping: !user.enableBackendUrlMapping,
            }}
          />
        </DefinitionTeam>
        <DefinitionTeam label="JavaScriptタグ管理（モジュール）">
          <AccountSwitch
            isChecked={user.jsTagManagementEnabled}
            input={{
              jsTagManagementEnabled: !user.jsTagManagementEnabled,
            }}
          />
        </DefinitionTeam>
        {user.jsTagManagementEnabled ? (
          <DefinitionTeam label="JavaScriptタグ">
            <Text fontSize="14px">
              サイト設定の「タグ」タブで管理します
              {scriptTagSettingsPath != null && (
                <>
                  （
                  <Link
                    as={NavLink}
                    to={scriptTagSettingsPath}
                    color="blue.500"
                    textDecoration="underline"
                  >
                    タグへ移動
                  </Link>
                  ）
                </>
              )}
            </Text>
          </DefinitionTeam>
        ) : (
          <DefinitionTeam label="Javascriptタグ">
            {user.userTag != null && user.userTag.length > 0
              ? "設定済み"
              : "未設定"}
          </DefinitionTeam>
        )}
      </SectionCard>
    </PageLayout>
  );
};
